// Core packages
import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';

// Third party packages
import { MatDialog, MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { FormlyFieldConfig, FormlyFormOptions } from '@ngx-formly/core';
import { TranslateService } from '@ngx-translate/core';
import { MatProgressButtonOptions } from 'mat-progress-buttons';
import { CookieService } from 'ngx-cookie-service';
import { ToastrService } from 'ngx-toastr';
import { concat, Observable, of, Subject, Subscription } from 'rxjs';
import { catchError, delay, distinctUntilChanged, switchMap, tap } from 'rxjs/operators';

// Custom packages
import { AuthService } from 'src/app/modules/auth/auth.service';
import { User } from 'src/app/shared/models/user/user.model';
import { ConfigService } from 'src/app/shared/services/config.service';

@Component({
  selector: 'app-choose-recipient-dialog',
  templateUrl: './choose-recipient-dialog.component.html',
  styleUrls: ['./choose-recipient-dialog.component.scss']
})
export class ChooseRecipientDialogComponent implements OnInit, OnDestroy {
  private subscriptions: Subscription[] = [];
  loggedUser = new User();
  lang = this.translateService.currentLang || this.translateService.defaultLang;

  // BEGIN - Add subUser form
  // BEGIN - ngx-formly
  form: FormGroup = new FormGroup({});
  model: any = {};
  fields: FormlyFieldConfig[] = [];
  options: FormlyFormOptions = {};
  // END - ngx-formly

  formBtnOptions: MatProgressButtonOptions = {
    active: false,
    text: this.translateService.instant('common.cta.confirm'),
    spinnerSize: 19,
    raised: true,
    stroked: true,
    flat: false,
    fab: false,
    buttonColor: 'primary',
    spinnerColor: 'primary',
    fullWidth: false,
    disabled: false,
    mode: 'indeterminate',
    buttonIcon: {
      fontSet: 'fal',
      fontIcon: 'fa-check',
      inline: true,
    },
  };
  // END - Add communication form

  propertyControl: FormControl = new FormControl('');
  propertyOptions: any[] = [];
  filteredOptions: Observable<string[]>;

  dialogIsLoading = true;
  displayedRecipients = [];
  filteredRecipients = [];
  selectedRecipients = [];
  selectAll = false;

  onDestroy$ = new Subject<void>();
  options$: Observable<any> = new Observable();
  optionsLoading = false;
  optionsInput$: Subject<string> = new Subject<string>();
  selectedOption: any[] = [];
  value = null;
  placeholder = '';
  typeToSearchText = '';
  clearAllText = '';
  notFoundText = '';
  loadingText = '';

  searchValue = "";

  /**
   * Class constructor
   */
  constructor(
    public dialogRef: MatDialogRef<ChooseRecipientDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: any,
    public confirmDialog: MatDialog,
    public configService: ConfigService,

    private cookieService: CookieService,
    private toastrService: ToastrService,
    private translateService: TranslateService,
    private authService: AuthService,
  ) { }

  /**
   * Init component
   *
   */
  ngOnInit(): void {
    if (!this.data || (this.data && !this.data.phonebook)) {
      this.toastrService.error(this.translateService.instant('common.words.display_no_data'));
    }

    // Get logged user data
    this.subscriptions.push(this.authService.loggedUser$.subscribe(res => this.loggedUser.setData(res)));

    // Get current lang
    this.lang = this.cookieService.get('lang') || this.configService.defaultLang;

    // Get phonebook recipients
    this.displayedRecipients = this.data.phonebook;
    this.selectedRecipients = this.data.model ? this.data.model : [];

    if (this.selectedRecipients.length) {
      this.displayedRecipients.forEach((rcpnt: any) => {
        if (this.selectedRecipients.includes(rcpnt.email)) {
          rcpnt["selected"] = true;
        } else {
          rcpnt["selected"] = false;
        }
      });
    } else {
      this.displayedRecipients.forEach((rcpnt: any) => {
        rcpnt["selected"] = false;
      });
    }

    if (this.displayedRecipients.every(rcpnt => rcpnt.selected)) {
      this.selectAll = true;
    }

    this.filteredRecipients = JSON.parse(JSON.stringify(this.displayedRecipients));

    // Stop loading dialog
    setTimeout(() => {
      this.dialogIsLoading = false;
    }, 500);

    if (this.data.isMaster) {
      this.placeholder = this.translateService.instant(
        "properties.searchProperty"
      );
      this.typeToSearchText = this.translateService.instant(
        "common.words.typeToSearch",
        { minTermLength: 1 }
      );
      this.clearAllText = this.translateService.instant(
        "common.words.clearAll"
      );
      this.notFoundText = this.translateService.instant(
        "common.words.notFound"
      );
      this.loadingText = this.translateService.instant(
        "common.words.loading"
      );
      if (this.data.propertiesMaster && Array.isArray(this.data.propertiesMaster)) {
        this.data.propertiesMaster.forEach((item: any) => this.propertyOptions.push({
          id: item._id,
          name: item.name
        }));
      }

      this.options$ = concat(
        of([]),
        this.optionsInput$.pipe(
          distinctUntilChanged(), // Eliminate duplicate values
          tap(() => this.optionsLoading = true), // Start loader
          switchMap(term => this.getOption(term).pipe(
            catchError(() => of([])), // empty list on error
            tap(() => this.optionsLoading = false)
        ))
        )
      )

    }

  }

  getOption(term: string = null): Observable<any[]> {
    let items = this.propertyOptions;
    if (term) {
        items = items.filter(x => x.name.toLocaleLowerCase().indexOf(term.toLocaleLowerCase()) > -1);
    }
    return of(items).pipe(delay(500));
}


  /**
   * Fired every time model data changes
   *
   * @param {*} data new model data
   */
   onChange(data: any): void {
    const filterValue = data && data.name ? data.name : '';
    if (filterValue !== '') {
      const pattern = new RegExp(filterValue, 'i');
      this.displayedRecipients = this.data.phonebook.filter((item: any) => 
        item.propertyId && item.propertyId.name && pattern.test(item.propertyId.name)
      );
    } else {
      this.displayedRecipients = this.data.phonebook;
    }
    this.filteredRecipients = JSON.parse(JSON.stringify(this.displayedRecipients));
    this.selectAll = false;
    this.onSelectAll(false);
  }

  /**
   * Handle component destroy
   *
   * @since 1.0.0
   */
  ngOnDestroy(): void {
    // Unsubscribe from all subscriptions
    this.subscriptions.forEach(sub => sub.unsubscribe());
  }

  /**
   * Handle dialog closing
   *
   * @since 1.0.0
   */
  onClose(): void {
    this.dialogRef.close();
  }

  /**
   * Handle form submit
   *
   * @since 1.0.0
   */
  onFormSubmit(): void {
    // Prevent invalid form submit
    if (this.form.invalid) {
      Object.keys(this.form.controls).forEach(key => {
        this.form.controls[key].markAsTouched();
      });
      this.toastrService.error(this.translateService.instant('common.errors.form_is_invalid'));
      return;
    }

    // if (this.displayedRecipients.every(rcpnt => !rcpnt.selected)) {
    //   this.selectedRecipients = [];
    // } else {
    //   this.displayedRecipients.forEach((recipient: any) => {
    //     // if (!recipient.selected || recipient.selected === false) {
    //     //   return;
    //     // }
    //     if (this.selectedRecipients && this.selectedRecipients.includes(recipient.email)) {
    //       return;
    //     }
    //     this.selectedRecipients.push(recipient.email);
    //   });
    // }

    this.selectedRecipients = this.filteredRecipients
      .filter(recipient => recipient.selected)
      .map(recipient => recipient.email);

    this.dialogRef.close(this.selectedRecipients);
  }

  /**
   * Update recipients array with incoming element
   *
   * @since 1.0.0
   */
  onRecipientSelected(recipient: any, checked: boolean): void {
    if (recipient && checked) {
      this.filteredRecipients.forEach((rcpnt: any) => {
        if (rcpnt === recipient) {
          rcpnt["selected"] = true;
        }
      });
    } else if (recipient && !checked) {
      this.filteredRecipients.forEach((rcpnt: any) => {
        if (rcpnt === recipient) {
          rcpnt["selected"] = false;
        }
      });
    }
  }

  /**
   * Handle select all recipients
   *
   * @since 1.0.0
   */
  onSelectAll(checked: boolean): void {
    this.selectAll = checked;
    if (!checked) {
      this.filteredRecipients.forEach((recipient: any) => {
        recipient["selected"] = false;
      });
    } else if (checked) {
      this.filteredRecipients.forEach((recipient: any) => {
        recipient["selected"] = true;
        // this.selectedRecipients.push(recipient.email);
      });
    }
  }
  
  /**
   * Reset given filter value and reload the page
   *
   * @since 1.0.0
   */
   onResetFilter(): void {
    this.propertyControl.setValue('');
  }

  
  /**
   * Handle filter on email
   *
   * @since 1.0.0
   */
   applyFilter(event: Event): void {
    this.searchValue = (event.target as HTMLInputElement).value;
    const regex = new RegExp(this.searchValue.trim().toLowerCase(), "gi");

    this.filteredRecipients = 
    this.displayedRecipients. filter((el: any) => {
      if (
        (el.firstName && el.firstName.search(regex) > -1) ||
        (el.lastName && el.lastName.search(regex) > -1) ||
        (el.email && el.email.search(regex) > -1)
      ) {
        return true;
      }
      
      return false;
    });
  }
}
